import { KeyboardEvent, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  useGetProjects,
  useUpdateProject,
} from '@/service-library/hooks/projects'
import { Project } from '@/types/projects'
import { FormTextField, PixyDocsForm } from '@/components/forms'

type FormValues = {
  name: string
}

type ProjectNameFieldProps = {
  /** Put on the input, so a `SettingsRow` label can point at it. */
  id: string
  project: Project
  updateProject: ReturnType<typeof useUpdateProject>['updateProject']
}

/**
 * The project name, edited in place. A valid name saves on Enter or when the
 * field loses focus. Escape puts back the saved name.
 */
export default function ProjectNameField({
  id,
  project,
  updateProject,
}: ProjectNameFieldProps) {
  const { projects } = useGetProjects({
    refetchOnWindowFocus: false,
    filters: {
      org_id: project.org_id,
      fields__only: 'id,name',
      limit: '1000',
    },
  })

  const methods = useForm<FormValues>({
    defaultValues: { name: project.name },
    mode: 'onChange',
  })

  const { formState, handleSubmit, reset } = methods
  const nameError = formState.errors.name

  // Follow the saved name, including the rollback when a save fails.
  useEffect(() => {
    reset({ name: project.name })
  }, [project.name, reset])

  function onSubmit({ name }: FormValues) {
    const trimmedName = name.trim()
    if (trimmedName === project.name) return
    updateProject({ ...project, name: trimmedName })
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return
    reset({ name: project.name })
    ;(e.target as HTMLElement).blur()
  }

  return (
    <PixyDocsForm methods={methods} onSubmit={onSubmit} id="project-name-form">
      <FormTextField
        id={id}
        name="name"
        variant="outlined"
        size="small"
        required
        requiredErrorText="Name cannot be empty."
        error={!!nameError}
        helperText={nameError?.message}
        onBlur={() => handleSubmit(onSubmit)()}
        onKeyDown={handleKeyDown}
        sx={{ width: 280 }}
        useControllerProps={{
          rules: {
            validate: (value: string) => {
              if (!value.trim().length) return 'Name cannot be empty.'

              if (
                projects.some(
                  ({ id: projectId, name }) =>
                    name.toLowerCase() === value.toLowerCase().trim() &&
                    projectId !== project.id,
                )
              )
                return 'Name must be unique.'

              return true
            },
          },
        }}
      />
    </PixyDocsForm>
  )
}
